import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { getCharacterVersions, createCharacterVersion, restoreCharacterVersion, updateCharacter, generateCharacterSheet, detectCharacters, createCharacter } from "@/services/characterService";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { IconCharacters } from "@/components/Icons";
import { Wand2, Loader2, User, Save, Plus, Camera, Pencil, X, Shirt, ChevronLeft, ChevronRight, Layers, RefreshCw, Maximize2, Eye, Search, Grid3x3, LayoutList, Check, Upload, MessageSquare, Sparkles } from "lucide-react";

const CHARACTER_ATTRIBUTES = [
  { key: "eyes", label: "Eyes", keywords: ["eyes", "eye color", "eye colour", "gaze", "iris", "pupils", "eyelids", "eyebrows"] },
  { key: "hair", label: "Hair", keywords: ["hair", "hairstyle", "hair color", "hair colour", "bald", "curly", "straight hair", "wavy hair", "ponytail", "braids", "bangs", "locks"] },
  { key: "face", label: "Face Structure", keywords: ["face", "jawline", "cheekbones", "forehead", "chin", "nose", "lips", "facial features", "oval face", "round face", "square face", "angular"] },
  { key: "age", label: "Age", keywords: ["age", "years old", "young", "middle-aged", "elderly", "teenager", "twenties", "thirties", "forties", "fifties", "sixties", "teen", "child", "adult", "old"] },
  { key: "race", label: "Race / Ethnicity", keywords: ["race", "ethnicity", "caucasian", "african", "asian", "hispanic", "latino", "indian", "middle eastern", "mixed race", "white", "black", "korean", "japanese", "chinese", "european", "south asian"] },
  { key: "skin", label: "Skin Tone", keywords: ["skin", "complexion", "pale", "fair", "olive", "tan", "dark skin", "light skin", "brown skin", "freckles", "moles"] },
  { key: "accessories", label: "Accessories", keywords: ["glasses", "earrings", "necklace", "watch", "ring", "bracelet", "hat", "cap", "scarf", "piercing", "tattoo", "scar", "accessory", "accessories", "jewelry"] },
  { key: "facial_hair", label: "Facial Hair", keywords: ["beard", "mustache", "moustache", "goatee", "stubble", "clean-shaven", "clean shaven", "sideburns", "facial hair", "five o'clock shadow"] },
  { key: "build", label: "Build / Physique", keywords: ["muscular", "slim", "thin", "athletic", "stocky", "heavyset", "petite", "tall", "short", "lean", "broad", "build", "physique", "height", "weight", "fit", "overweight", "skinny"] },
];

function checkAttributes(text) {
  const lower = (text || "").toLowerCase();
  return CHARACTER_ATTRIBUTES.map(attr => ({
    ...attr,
    checked: attr.keywords.some(kw => lower.includes(kw)),
  }));
}

// Compact Character Thumbnail for the selector strip
function CharacterThumb({ character, isSelected, onClick }) {
  const hasSheet = character.default_character_sheet_photo || character.default_character_sheet_sketch || character.default_character_sheet;
  const thumbUrl = hasSheet ? `data:image/png;base64,${character.default_character_sheet_photo || character.default_character_sheet}` : null;
  
  return (
    <motion.button
      onClick={onClick}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      className={`flex-shrink-0 relative group ${isSelected ? '' : ''}`}
      data-testid={`char-thumb-${character.id}`}
    >
      <div className={`w-16 h-20 rounded-xl overflow-hidden border-2 transition-all ${
        isSelected 
          ? 'border-[var(--color-gold-500)] ring-2 ring-[var(--color-gold-500)]/30' 
          : 'border-transparent hover:border-[var(--border-strong)]'
      }`}>
        {thumbUrl ? (
          <img src={thumbUrl} alt={character.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-[var(--bg-muted)] flex items-center justify-center">
            <User size={20} className="text-[var(--text-disabled)]" />
          </div>
        )}
      </div>
      <p className={`text-[10px] mt-1.5 text-center truncate w-16 ${
        isSelected ? 'text-[var(--color-gold-400)] font-medium' : 'text-[var(--text-muted)]'
      }`}>
        {character.name}
      </p>
      {!hasSheet && (
        <span className="absolute -top-1 -right-1 w-3 h-3 bg-[var(--color-warning)] rounded-full border-2 border-[var(--bg-surface)]" />
      )}
    </motion.button>
  );
}

// Large Image Preview with Lightbox
function ImagePreview({ src, alt, label, onRegenerate, isGenerating }) {
  const [showLightbox, setShowLightbox] = useState(false);
  
  return (
    <>
      <div className="relative group">
        {/* Use auto height to show full image without cropping */}
        <div className="rounded-2xl overflow-hidden bg-[var(--bg-muted)] border border-[var(--border-subtle)]">
          {src ? (
            <img 
              src={src} 
              alt={alt} 
              className="w-full h-auto"
              style={{ maxHeight: '70vh' }}
            />
          ) : (
            <div className="aspect-[3/4] flex flex-col items-center justify-center">
              <User size={48} className="text-[var(--text-disabled)] mb-3" />
              <p className="text-sm text-[var(--text-muted)]">Not generated</p>
            </div>
          )}
        </div>
        
        {/* Label - positioned below image */}
        <div className="mt-3 flex items-center justify-between">
          <span className="text-sm font-medium text-[var(--color-gold-400)]">{label}</span>
          <div className="flex gap-2">
            {src && (
              <button
                onClick={() => setShowLightbox(true)}
                className="btn-ghost btn-icon-xs"
                title="View full size"
              >
                <Maximize2 size={14} />
              </button>
            )}
            <Button
              onClick={onRegenerate}
              disabled={isGenerating}
              size="sm"
              className="btn-ghost text-xs"
            >
              {isGenerating ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
              <span className="ml-1">Regenerate</span>
            </Button>
          </div>
        </div>
      </div>
      
      {/* Lightbox - full screen view */}
      <AnimatePresence>
        {showLightbox && src && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
            onClick={() => setShowLightbox(false)}
          >
            <button 
              className="absolute top-6 right-6 btn-ghost btn-icon text-white hover:bg-white/10"
              onClick={() => setShowLightbox(false)}
            >
              <X size={24} />
            </button>
            <img 
              src={src} 
              alt={alt} 
              className="max-w-[95vw] max-h-[95vh] object-contain rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
            <p className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/70 text-sm">
              {label} • Click outside or X to close
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

// Scene Look Card
function SceneLookCard({ sceneNum, style, imageData, onView }) {
  return (
    <motion.div
      whileHover={{ scale: 1.03 }}
      className="relative group cursor-pointer"
      onClick={onView}
    >
      <div className="aspect-[3/4] rounded-xl overflow-hidden bg-[var(--bg-muted)]">
        <img src={`data:image/png;base64,${imageData}`} alt={`Scene ${sceneNum}`} className="w-full h-full object-cover" />
      </div>
      <div className="absolute bottom-2 left-2 flex gap-1">
        <span className="chip chip-gold text-[9px]">S{sceneNum}</span>
        <span className="chip text-[9px]">{style}</span>
      </div>
      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl flex items-center justify-center">
        <Eye size={24} className="text-white" />
      </div>
    </motion.div>
  );
}

// Full Character Detail View
function CharacterDetailView({ character, scenes, onUpdate, onClose, onPrev, onNext, hasPrev, hasNext }) {
  const [description, setDescription] = useState(character?.description || "");
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState({ photo: false, sketch: false, both: false });
  const [activeTab, setActiveTab] = useState("base");
  const [costumeDesc, setCostumeDesc] = useState({});
  const [generatingCostume, setGeneratingCostume] = useState({});
  const [versions, setVersions] = useState([]);
  const [versionName, setVersionName] = useState("");
  const [showVersions, setShowVersions] = useState(false);
  const [savingVersion, setSavingVersion] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => { return () => { mountedRef.current = false; }; }, []);

  useEffect(() => {
    if (character) {
      setDescription(character.description || "");
      loadVersions();
    }
  }, [character]);

  const loadVersions = async () => {
    if (!character) return;
    try {
      const data = await getCharacterVersions(character.id);
      setVersions(data || []);
    } catch { setVersions([]); }
  };

  const handleSaveVersion = async () => {
    if (!versionName.trim()) { toast.error("Enter a version name"); return; }
    setSavingVersion(true);
    try {
      await createCharacterVersion(character.id, { name: versionName });
      toast.success(`Version "${versionName}" saved`);
      setVersionName("");
      loadVersions();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save version");
    } finally { setSavingVersion(false); }
  };

  const handleRestoreVersion = async (versionId) => {
    try {
      await restoreCharacterVersion(character.id, { version_id: versionId });
      toast.success("Version restored");
      onUpdate();
    } catch { toast.error("Restore failed"); }
  };

  if (!character) return null;

  const photoSheet = character.default_character_sheet_photo;
  const sketchSheet = character.default_character_sheet_sketch;
  const hasBaseSheet = photoSheet || sketchSheet || character.default_character_sheet;
  
  const sceneLooks = character.scene_character_sheets || {};
  const lookKeys = Object.keys(sceneLooks);

  const attributes = checkAttributes(description);
  const checkedCount = attributes.filter(a => a.checked).length;

  // Find scenes this character appears in
  const characterScenes = (scenes || []).filter(s =>
    s.characters?.some(c => c.toLowerCase() === character.name.toLowerCase())
  );

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateCharacter(character.id, { description });
      toast.success("Saved");
      onUpdate();
    } catch (error) {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleGenerate = async (style) => {
    if (!description.trim()) {
      toast.error("Please add a description first");
      return;
    }
    
    setGenerating(prev => ({ ...prev, [style]: true }));
    try {
      await updateCharacter(character.id, { description });
      await generateCharacterSheet(character.id, {
        attire: "default",
        style: style === "both" ? "photorealistic" : style,
        is_base: true
      });
      
      if (style === "both") {
        await generateCharacterSheet(character.id, {
          attire: "default",
          style: "sketch",
          is_base: true
        });
      }
      
      toast.success(style === "both" ? "Both styles generated!" : `${style === 'photorealistic' ? 'Photo' : 'Sketch'} generated!`);
      if (mountedRef.current) onUpdate();
    } catch (error) {
      toast.error("Generation failed - please try again");
    } finally {
      if (mountedRef.current) setGenerating(prev => ({ ...prev, [style]: false }));
    }
  };

  const handleGenerateCostume = async (sceneNum) => {
    const desc = costumeDesc[sceneNum];
    if (!desc?.trim()) {
      toast.error("Add a costume description first");
      return;
    }
    setGeneratingCostume(prev => ({ ...prev, [sceneNum]: true }));
    try {
      await generateCharacterSheet(character.id, {
        attire: desc,
        style: "photorealistic",
        is_base: false,
        scene_number: sceneNum
      });
      toast.success(`Costume look for Scene ${sceneNum} generated!`);
      if (mountedRef.current) onUpdate();
    } catch (error) {
      toast.error("Costume generation failed");
    } finally {
      if (mountedRef.current) setGeneratingCostume(prev => ({ ...prev, [sceneNum]: false }));
    }
  };

  const handleUploadReference = async (sceneNum, file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (e) => {
      const base64 = e.target.result.split(',')[1];
      try {
        await generateCharacterSheet(character.id, {
          attire: costumeDesc[sceneNum] || `Scene ${sceneNum} costume`,
          style: "photorealistic",
          is_base: false,
          scene_number: sceneNum,
          reference_image: base64
        });
        toast.success("Reference uploaded & costume generated!");
        if (mountedRef.current) onUpdate();
      } catch (error) {
        toast.error("Upload failed");
      }
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="h-16 px-6 border-b border-[var(--border-subtle)] bg-[var(--bg-surface)] flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1">
            <button onClick={onPrev} disabled={!hasPrev} className="btn-ghost btn-icon-sm disabled:opacity-30" data-testid="prev-character">
              <ChevronLeft size={20} />
            </button>
            <button onClick={onNext} disabled={!hasNext} className="btn-ghost btn-icon-sm disabled:opacity-30" data-testid="next-character">
              <ChevronRight size={20} />
            </button>
          </div>
          <div>
            <h2 className="text-xl font-semibold text-[var(--text-primary)]">{character.name}</h2>
            <p className="text-xs text-[var(--text-muted)]">
              {hasBaseSheet ? "Base sheet ready" : "Needs base sheet"} · {lookKeys.length} scene look{lookKeys.length !== 1 ? 's' : ''} · {checkedCount}/{attributes.length} attributes
            </p>
          </div>
        </div>
        <button onClick={onClose} className="btn-ghost btn-icon" data-testid="close-character-view">
          <X size={20} />
        </button>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-hidden flex">
        {/* Left Side - Images */}
        <div className="flex-1 p-6 overflow-y-auto">
          {/* Tabs */}
          <div className="flex gap-2 mb-6">
            <button
              onClick={() => setActiveTab("base")}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeTab === "base"
                  ? 'bg-[var(--color-gold-500)] text-[var(--color-base-950)]'
                  : 'bg-[var(--bg-overlay)] text-[var(--text-secondary)] hover:bg-[var(--bg-muted)]'
              }`}
            >
              Base Sheets
            </button>
            <button
              onClick={() => setActiveTab("looks")}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeTab === "looks"
                  ? 'bg-[var(--color-gold-500)] text-[var(--color-base-950)]'
                  : 'bg-[var(--bg-overlay)] text-[var(--text-secondary)] hover:bg-[var(--bg-muted)]'
              }`}
            >
              Scene Looks ({characterScenes.length})
            </button>
          </div>

          {activeTab === "base" ? (
            <div className="grid grid-cols-2 gap-6 max-w-3xl">
              <ImagePreview
                src={photoSheet ? `data:image/png;base64,${photoSheet}` : null}
                alt={`${character.name} - Photo`}
                label="Photorealistic"
                onRegenerate={() => handleGenerate("photorealistic")}
                isGenerating={generating.photo}
              />
              <ImagePreview
                src={sketchSheet ? `data:image/png;base64,${sketchSheet}` : null}
                alt={`${character.name} - Sketch`}
                label="Sketch"
                onRegenerate={() => handleGenerate("sketch")}
                isGenerating={generating.sketch}
              />
            </div>
          ) : (
            /* Scene Looks — one card per scene the character is in */
            <div className="space-y-6 max-w-3xl">
              {characterScenes.length === 0 ? (
                <div className="text-center py-16">
                  <Shirt size={40} className="mx-auto text-[var(--text-disabled)] mb-4" />
                  <h3 className="text-base font-medium text-[var(--text-primary)] mb-2">No Scenes Found</h3>
                  <p className="text-sm text-[var(--text-muted)] max-w-sm mx-auto">
                    This character doesn't appear in any detected scenes yet. Run scene detection from the Scenes tab.
                  </p>
                </div>
              ) : (
                characterScenes.map(scene => {
                  const lookKey = `${scene.number}_photorealistic`;
                  const existingLook = sceneLooks[lookKey];
                  const isGeneratingThis = generatingCostume[scene.number];
                  return (
                    <div key={scene.number} className="bg-[var(--bg-overlay)] rounded-xl border border-[var(--border-subtle)] overflow-hidden" data-testid={`scene-look-${scene.number}`}>
                      {/* Scene header */}
                      <div className="px-5 py-3 border-b border-[var(--border-subtle)] bg-[var(--bg-surface)]">
                        <div className="flex items-center gap-2">
                          <span className="chip chip-gold text-[10px]">Scene {scene.number}</span>
                          <span className="text-xs text-[var(--text-primary)] font-medium truncate">{scene.heading || scene.location}</span>
                        </div>
                        {scene.content && (
                          <p className="text-[10px] text-[var(--text-muted)] mt-1 line-clamp-2">{scene.content.slice(0, 120)}</p>
                        )}
                      </div>
                      
                      <div className="p-5 flex gap-5">
                        {/* Existing look or empty state */}
                        <div className="w-48 flex-shrink-0">
                          {existingLook ? (
                            <div className="aspect-[3/4] rounded-xl overflow-hidden bg-[var(--bg-muted)] border border-[var(--border-subtle)]">
                              <img src={`data:image/png;base64,${existingLook}`} alt={`Scene ${scene.number} look`} className="w-full h-full object-cover" />
                            </div>
                          ) : (
                            <div className="aspect-[3/4] rounded-xl bg-[var(--bg-muted)] border border-dashed border-[var(--border-default)] flex flex-col items-center justify-center">
                              <Shirt size={28} className="text-[var(--text-disabled)] mb-2" />
                              <p className="text-[10px] text-[var(--text-muted)]">No costume yet</p>
                            </div>
                          )}
                        </div>

                        {/* Costume design controls */}
                        <div className="flex-1 space-y-3">
                          <div>
                            <label className="text-[10px] font-medium text-[var(--text-muted)] uppercase tracking-wider mb-1.5 block">Costume Description</label>
                            <Textarea
                              value={costumeDesc[scene.number] || ""}
                              onChange={(e) => setCostumeDesc(prev => ({ ...prev, [scene.number]: e.target.value }))}
                              placeholder={`Describe ${character.name}'s costume in Scene ${scene.number}... e.g., worn leather jacket, white t-shirt, dark jeans`}
                              className="input resize-none h-20 text-xs"
                              data-testid={`costume-desc-${scene.number}`}
                            />
                          </div>
                          <div className="flex gap-2">
                            <Button
                              onClick={() => handleGenerateCostume(scene.number)}
                              disabled={isGeneratingThis || !costumeDesc[scene.number]?.trim()}
                              className="btn-primary flex-1 text-xs"
                              size="sm"
                              data-testid={`generate-costume-${scene.number}`}
                            >
                              {isGeneratingThis ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
                              <span className="ml-1.5">Generate Costume</span>
                            </Button>
                            <label className="btn-secondary flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg cursor-pointer" data-testid={`upload-ref-${scene.number}`}>
                              <Upload size={13} />
                              <span>Upload Ref</span>
                              <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={(e) => handleUploadReference(scene.number, e.target.files?.[0])}
                              />
                            </label>
                          </div>
                          {existingLook && (
                            <Button
                              onClick={() => handleGenerate("photorealistic")}
                              variant="ghost"
                              className="text-[10px] text-[var(--text-muted)] hover:text-[var(--text-primary)]"
                              size="sm"
                            >
                              <RefreshCw size={11} />
                              <span className="ml-1">Regenerate</span>
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>

        {/* Right Side - Description & Actions */}
        <div className="w-96 border-l border-[var(--border-subtle)] bg-[var(--bg-surface)] flex flex-col flex-shrink-0">
          <ScrollArea className="flex-1">
            <div className="p-6 space-y-6">
              {/* Description */}
              <div>
                <label className="label mb-2 block">Physical Description</label>
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe physical appearance: face shape, skin tone, hair, build, age, distinguishing features..."
                  className="input resize-none h-36"
                  data-testid="character-description"
                />
              </div>

              {/* Attribute Checklist */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-[10px] font-medium text-[var(--text-muted)] uppercase tracking-wider">Attribute Coverage</label>
                  <span className={`text-[10px] font-bold ${checkedCount === attributes.length ? 'text-emerald-400' : checkedCount >= 5 ? 'text-[var(--color-gold-400)]' : 'text-[var(--text-muted)]'}`}>
                    {checkedCount}/{attributes.length}
                  </span>
                </div>
                {/* Progress bar */}
                <div className="h-1 bg-[var(--bg-muted)] rounded-full mb-3 overflow-hidden">
                  <motion.div
                    className={`h-full rounded-full ${checkedCount === attributes.length ? 'bg-emerald-500' : 'bg-[var(--color-gold-500)]'}`}
                    initial={{ width: 0 }}
                    animate={{ width: `${(checkedCount / attributes.length) * 100}%` }}
                    transition={{ duration: 0.3 }}
                  />
                </div>
                <div className="grid grid-cols-2 gap-1.5" data-testid="attribute-checklist">
                  {attributes.map(attr => (
                    <div
                      key={attr.key}
                      className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-[11px] transition-all ${
                        attr.checked
                          ? 'bg-emerald-500/10 text-emerald-400'
                          : 'bg-[var(--bg-overlay)] text-[var(--text-disabled)]'
                      }`}
                    >
                      <div className={`w-3.5 h-3.5 rounded flex items-center justify-center flex-shrink-0 ${
                        attr.checked ? 'bg-emerald-500' : 'border border-[var(--border-default)]'
                      }`}>
                        {attr.checked && <Check size={9} className="text-black" />}
                      </div>
                      <span className={attr.checked ? 'font-medium' : ''}>{attr.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Generate Actions */}
              <div className="space-y-3">
                <label className="label mb-2 block">Generate Base Sheets</label>
                
                <Button
                  onClick={() => handleGenerate("both")}
                  disabled={generating.both || generating.photo || generating.sketch || !description.trim()}
                  className="btn-primary w-full justify-center py-4"
                  data-testid="generate-both-btn"
                >
                  {generating.both ? <Loader2 size={18} className="animate-spin" /> : <Layers size={18} />}
                  <span className="ml-2">Generate Both Styles</span>
                </Button>
                
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    onClick={() => handleGenerate("photorealistic")}
                    disabled={generating.photo || generating.both || !description.trim()}
                    className="btn-secondary justify-center"
                    data-testid="generate-photo-btn"
                  >
                    {generating.photo ? <Loader2 size={16} className="animate-spin" /> : <Camera size={16} />}
                    <span className="ml-2">Photo</span>
                  </Button>
                  <Button
                    onClick={() => handleGenerate("sketch")}
                    disabled={generating.sketch || generating.both || !description.trim()}
                    className="btn-secondary justify-center"
                    data-testid="generate-sketch-btn"
                  >
                    {generating.sketch ? <Loader2 size={16} className="animate-spin" /> : <Pencil size={16} />}
                    <span className="ml-2">Sketch</span>
                  </Button>
                </div>
              </div>

              <div className="divider" />

              {/* Save */}
              <Button 
                onClick={handleSave} 
                disabled={saving} 
                className="btn-ghost w-full justify-center border border-[var(--border-default)]"
                data-testid="save-description-btn"
              >
                {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                <span className="ml-2">Save Description</span>
              </Button>

              {/* Versioning */}
              <div>
                <button
                  onClick={() => setShowVersions(!showVersions)}
                  className="flex items-center justify-between w-full text-xs text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
                  data-testid="toggle-versions-btn"
                >
                  <span className="font-medium">Versions ({versions.length})</span>
                  <Layers size={14} />
                </button>

                {showVersions && (
                  <div className="mt-3 space-y-3">
                    {/* Save new version */}
                    <div className="flex gap-2">
                      <Input
                        value={versionName}
                        onChange={(e) => setVersionName(e.target.value)}
                        placeholder="e.g., v1 — bearded look"
                        className="input h-8 text-xs flex-1"
                        onKeyDown={(e) => e.key === 'Enter' && handleSaveVersion()}
                        data-testid="version-name-input"
                      />
                      <Button onClick={handleSaveVersion} disabled={savingVersion || !versionName.trim()} className="btn-primary h-8" size="sm" data-testid="save-version-btn">
                        {savingVersion ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                      </Button>
                    </div>
                    {/* Version history */}
                    {versions.length > 0 && (
                      <div className="space-y-1.5 max-h-40 overflow-y-auto">
                        {versions.map((v, i) => (
                          <div key={v.id || i} className="flex items-center justify-between p-2 bg-[var(--bg-overlay)] rounded-lg">
                            <div>
                              <p className="text-[11px] font-medium text-[var(--text-primary)]">{v.name}</p>
                              <p className="text-[9px] text-[var(--text-muted)]">{new Date(v.created_at).toLocaleDateString()}</p>
                            </div>
                            <Button onClick={() => handleRestoreVersion(v.id)} variant="ghost" size="sm" className="text-[10px] h-6 text-[var(--color-gold-400)]" data-testid={`restore-version-${i}`}>
                              <RefreshCw size={10} />
                              <span className="ml-1">Restore</span>
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="pb-16" />
            </div>
          </ScrollArea>
        </div>
      </div>
    </div>
  );
}

// Main Characters Page
export default function CharactersPage({ projectId, characters, script, scenes, onUpdate }) {
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newName, setNewName] = useState("");
  const [detecting, setDetecting] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedView, setExpandedView] = useState(false);

  const selectedCharacter = selectedIndex !== null ? characters[selectedIndex] : null;

  const filteredCharacters = characters.filter(c =>
    c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleDetect = async () => {
    setDetecting(true);
    try {
      const data = await detectCharacters(projectId);
      toast.success(data.message);
      onUpdate();
    } catch (error) {
      toast.error("Detection failed");
    } finally {
      setDetecting(false);
    }
  };

  const handleCreate = async () => {
    if (!newName.trim()) return;
    try {
      await createCharacter({
        project_id: projectId,
        name: newName,
        description: ""
      });
      toast.success("Character created");
      setNewName("");
      setShowCreateDialog(false);
      onUpdate();
    } catch (error) {
      toast.error("Failed to create");
    }
  };

  // Update selected character when list changes
  useEffect(() => {
    if (selectedIndex !== null && selectedIndex >= characters.length) {
      setSelectedIndex(characters.length > 0 ? characters.length - 1 : null);
    }
  }, [characters, selectedIndex]);

  // No characters - show empty state
  if (characters.length === 0) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="empty-state-icon mx-auto mb-8">
            <IconCharacters size={48} />
          </div>
          <h2 className="h2 text-[var(--text-primary)] mb-4">No Characters Yet</h2>
          <p className="text-[var(--text-muted)] mb-8 leading-relaxed">
            Add characters manually or detect them automatically from your script. Each character can have base reference sheets and scene-specific costume looks.
          </p>
          <div className="flex gap-3 justify-center">
            <Button onClick={handleDetect} disabled={detecting} className="btn-secondary">
              {detecting ? <Loader2 size={16} className="animate-spin" /> : <Wand2 size={16} />}
              <span className="ml-2">Detect from Script</span>
            </Button>
            <Button onClick={() => setShowCreateDialog(true)} className="btn-primary">
              <Plus size={16} />
              <span className="ml-2">Add Character</span>
            </Button>
          </div>
        </div>
        
        {/* Create Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent className="bg-[var(--bg-surface)] border-[var(--border-default)] max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[var(--color-gold-500)]/10 flex items-center justify-center">
                  <User size={20} className="text-[var(--color-gold-400)]" />
                </div>
                Add Character
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Character name (e.g., JOHN)"
                className="input"
                autoFocus
                onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
                data-testid="new-character-name"
              />
              <Button onClick={handleCreate} disabled={!newName.trim()} className="btn-primary w-full" data-testid="create-character-btn">
                Create Character
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Character Selector Strip — scrollable + search + expand toggle */}
      <div className="border-b border-[var(--border-subtle)] bg-[var(--bg-surface)] flex-shrink-0">
        {/* Search + actions row */}
        <div className="px-6 pt-4 pb-2 flex items-center gap-3">
          <div className="relative flex-1 max-w-xs">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)] pointer-events-none z-10" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search characters..."
              className="input h-8 text-xs"
              style={{ paddingLeft: '2.25rem' }}
              data-testid="search-characters"
            />
          </div>
          <div className="flex items-center gap-1.5 ml-auto">
            <Button
              onClick={() => setExpandedView(!expandedView)}
              variant="ghost"
              size="sm"
              className={`btn-ghost h-8 w-8 p-0 ${expandedView ? 'text-[var(--color-gold-400)]' : ''}`}
              title={expandedView ? "Compact strip" : "Expand all"}
              data-testid="toggle-character-view"
            >
              {expandedView ? <LayoutList size={14} /> : <Grid3x3 size={14} />}
            </Button>
            <Button onClick={handleDetect} disabled={detecting} className="btn-ghost h-8 w-8 p-0" size="sm" data-testid="detect-characters-btn">
              {detecting ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />}
            </Button>
            <Button onClick={() => setShowCreateDialog(true)} className="btn-primary h-8" size="sm" data-testid="add-character-btn">
              <Plus size={14} />
              <span className="ml-1 text-xs">Add</span>
            </Button>
          </div>
        </div>

        {/* Character thumbnails — strip or grid */}
        <AnimatePresence mode="wait">
          {expandedView ? (
            <motion.div
              key="grid"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <div className="px-6 pb-4 grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-4" data-testid="character-grid-view">
                {filteredCharacters.map((char) => {
                  const idx = characters.indexOf(char);
                  const hasSheet = char.default_character_sheet_photo || char.default_character_sheet_sketch || char.default_character_sheet;
                  const thumbUrl = hasSheet ? `data:image/png;base64,${char.default_character_sheet_photo || char.default_character_sheet}` : null;
                  return (
                    <motion.button
                      key={char.id}
                      onClick={() => setSelectedIndex(idx)}
                      whileHover={{ scale: 1.03 }}
                      className="flex flex-col items-center"
                    >
                      <div className={`w-full aspect-[3/4] rounded-xl overflow-hidden border-2 transition-all ${
                        selectedIndex === idx
                          ? 'border-[var(--color-gold-500)] ring-2 ring-[var(--color-gold-500)]/30'
                          : 'border-transparent hover:border-[var(--border-strong)]'
                      }`}>
                        {thumbUrl ? (
                          <img src={thumbUrl} alt={char.name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full bg-[var(--bg-muted)] flex items-center justify-center">
                            <User size={28} className="text-[var(--text-disabled)]" />
                          </div>
                        )}
                      </div>
                      <p className={`text-xs mt-2 text-center truncate w-full ${
                        selectedIndex === idx ? 'text-[var(--color-gold-400)] font-medium' : 'text-[var(--text-muted)]'
                      }`}>
                        {char.name}
                      </p>
                    </motion.button>
                  );
                })}
              </div>
            </motion.div>
          ) : (
            <motion.div key="strip" className="h-24 px-6 flex items-center">
              <div className="flex-1 overflow-x-auto scrollbar-thin" data-testid="character-strip-view">
                <div className="flex gap-4 py-2 min-w-0">
                  {filteredCharacters.map((char) => {
                    const idx = characters.indexOf(char);
                    return (
                      <CharacterThumb
                        key={char.id}
                        character={char}
                        isSelected={selectedIndex === idx}
                        onClick={() => setSelectedIndex(idx)}
                      />
                    );
                  })}
                  {filteredCharacters.length === 0 && searchQuery && (
                    <p className="text-xs text-[var(--text-muted)] py-4">No characters match "{searchQuery}"</p>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-hidden">
        <AnimatePresence mode="wait">
          {selectedCharacter ? (
            <motion.div
              key={selectedCharacter.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="h-full"
            >
              <CharacterDetailView
                character={selectedCharacter}
                scenes={scenes}
                onUpdate={onUpdate}
                onClose={() => setSelectedIndex(null)}
                onPrev={() => setSelectedIndex(prev => Math.max(0, prev - 1))}
                onNext={() => setSelectedIndex(prev => Math.min(characters.length - 1, prev + 1))}
                hasPrev={selectedIndex > 0}
                hasNext={selectedIndex < characters.length - 1}
              />
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="h-full flex items-center justify-center"
            >
              <div className="text-center">
                <div className="w-20 h-20 rounded-2xl bg-[var(--bg-overlay)] flex items-center justify-center mx-auto mb-6 border border-[var(--border-default)]">
                  <User size={36} className="text-[var(--text-disabled)]" />
                </div>
                <h3 className="h3 text-[var(--text-primary)] mb-2">Select a Character</h3>
                <p className="text-sm text-[var(--text-muted)] max-w-sm">
                  Click on a character above to view and edit their details, or generate reference sheets.
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-[var(--bg-surface)] border-[var(--border-default)] max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[var(--color-gold-500)]/10 flex items-center justify-center">
                <User size={20} className="text-[var(--color-gold-400)]" />
              </div>
              Add Character
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Character name (e.g., JOHN)"
              className="input"
              autoFocus
              onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
              data-testid="new-character-name"
            />
            <Button onClick={handleCreate} disabled={!newName.trim()} className="btn-primary w-full" data-testid="create-character-btn">
              Create Character
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
